import switchIntervalCardContent from "@/components/shared/interval-switch/content/en/switch-interval-card.json";
import useAppQueryParam from "@/hooks/useAppQueryParam";
import { TConfiguredTracker } from "@/tracking/createTrackerConfig";

import { IS_DEV } from "@/data/constants";
import { PLAN_STATUS } from "@/data/plans";
import { createLogger } from "@/utils/createLogger";
import { DASHBOARD_MAIN_CONFIG } from "./config.dashboard";
import { DEFAULT_PRICING_VERSION } from "./constants.dashboard";
import dashboardPageContent from "./content/en/dashboard-page.json";
import dashboardSubscriptionListContent from "./content/en/dashboard-subscription-list.json";
import { TDashboardEvent } from "./events.dashboard";
import { TDashboardContent } from "./types.dashboard";

// ─── Types ───────────────────────────────────────────────────────────────────

type TDashboardServiceDeps = {
  getQueryParam: ReturnType<typeof useAppQueryParam>["getQueryParam"];
  track: TConfiguredTracker<TDashboardEvent>;
};

// ─── Content ─────────────────────────────────────────────────────────────────

const _Content: TDashboardContent = {
  ...dashboardPageContent,
  ...dashboardSubscriptionListContent,
  ...switchIntervalCardContent,
};

// ─── Factory ─────────────────────────────────────────────────────────────────

function createDashboardService() {
  let _initialized = false;

  // private state
  let _getQueryParam: TDashboardServiceDeps["getQueryParam"];
  let _track: TConfiguredTracker<TDashboardEvent>;
  let _enabledLogging = IS_DEV;

  const _Constants = {
    PLAN_STATUS,
    DEFAULT_PRICING_VERSION,
  };

  function init(deps: TDashboardServiceDeps) {
    if (_initialized) return;
    _initialized = true;

    _getQueryParam = deps.getQueryParam;
    _track = deps.track;
  }

  return {
    init,
    get getQueryParam() {
      return _getQueryParam;
    },
    get track() {
      return _track;
    },
    get Config() {
      return DASHBOARD_MAIN_CONFIG;
    },
    get Content() {
      return _Content;
    },
    get Constants() {
      return _Constants;
    },
    logger: createLogger("DASHBOARD", _enabledLogging),
  };
}

export const DashboardService = createDashboardService();

export type TDashboardService = typeof DashboardService;
